import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate, useSearchParams } from "react-router-dom";
import AdminLayout from '../layout/AdminLayout';
import { useAuth } from '../../context/AuthContext';
import { useNotificationCount } from '../../context/NotificationCountContext';
import { getDashboardCounts, getAmDashboardData, getSessionLoginDashboardData } from '../../models/ReportsModel';
import ConversionGraph from './DashboardCharts/ConversionGraph';
import DashboardTables from './DashboardCharts/DashboardTables';
import ActivePositionsPie from './DashboardCharts/ActivePositionsPie';
import RecruiterConversionGraph from "./DashboardCharts/RecruiterConversionGraph";
import AmMeetingStats from "./DashboardCharts/AmMeetingStats";
import * as model from '../../models/ReportsModel';
import { filterPipelineRows } from '../../helpers/DashboardPipelineHelper';
import { checkPermission, isSuperAdminUser } from '../../helpers/GeneralHelper';
import UserLoginFrequencyCharts from './DashboardCharts/UserLoginFrequencyCharts';
import { useLoginActivityAccess } from '../../hooks/useLoginActivityAccess';
import WorkflowHelpBanner from '../common/WorkflowHelpBanner';

function Dashboard() {
  const { notificationCount, setNotificationCount } = useNotificationCount();
  const navigate = useNavigate();
  const { state } = useAuth();
  let user_data = state.user_data;
//console.log("User Data in Dashboard:", user_data);
  const [greeting, setGreeting] = useState('');
  const [dashboardCounts, setDashboardCounts] = useState([]);
  const [interviewsList, setInterviewsList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [months, setMonths] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [pipelineRecruiterFilter, setPipelineRecruiterFilter] = useState('');
  const [pipelineManagerFilter, setPipelineManagerFilter] = useState('');
  const [amStats, setAmStats] = useState(null);
  const [amLoading, setAmLoading] = useState(false);
  const [loginActivityStats, setLoginActivityStats] = useState(null);
  const [loginActivityLoading, setLoginActivityLoading] = useState(false);

  const {
    canView: canViewLoginActivityDashboard,
    loading: loginActivityAccessLoading,
  } = useLoginActivityAccess();

  const filteredPipelineCounts = useMemo(
    () =>
      filterPipelineRows(
        dashboardCounts,
        pipelineRecruiterFilter,
        pipelineManagerFilter
      ),
    [dashboardCounts, pipelineRecruiterFilter, pipelineManagerFilter]
  );

  const onPipelineRecruiterFilterChange = useCallback((value) => {
    setPipelineRecruiterFilter(value);
  }, []);

  const onPipelineManagerFilterChange = useCallback((value) => {
    setPipelineManagerFilter(value);
  }, []);

  useEffect(() => {
    setPipelineRecruiterFilter('');
    setPipelineManagerFilter('');
  }, [selectedMonth?.value]);

  useEffect(() => {
    const hour = new Date().getHours();
    let newGreeting = '';

    if (hour >= 5 && hour < 12) {
      newGreeting = 'Good Morning ' + user_data.user_first_name;
    } else if (hour >= 12 && hour < 17) {
      newGreeting = 'Good Afternoon ' + user_data.user_first_name;
    } else {
      newGreeting = 'Good Evening ' + user_data.user_first_name;
    }

    setGreeting(newGreeting);
  }, []);

  const fetchUsers = async () => {
    try {
      setNotificationCount(localStorage.getItem('unread_notification_count'));
    } catch (error) {
      console.error(error);
    }
  };

  const getCounts = async (month) => {
    try {
      const result = await getDashboardCounts({ user_token: user_data.user_token, month: month });
      setDashboardCounts(Array.isArray(result?.counts) ? result.counts : []);
      setInterviewsList(result.interviews || []);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const getAmStats = async (month) => {
    if (!checkPermission('meetings', 'view')) {
      setAmStats(null);
      return;
    }
    setAmLoading(true);
    try {
      const result = await getAmDashboardData({
        user_token: user_data.user_token,
        month: month,
      });
      setAmStats(result || null);
    } catch (error) {
      console.error(error);
      setAmStats(null);
    } finally {
      setAmLoading(false);
    }
  };

  const getLoginActivityStats = async (month) => {
    if (loginActivityAccessLoading || !canViewLoginActivityDashboard) {
      setLoginActivityStats(null);
      return;
    }
    setLoginActivityLoading(true);
    try {
      const result = await getSessionLoginDashboardData({
        user_token: user_data.user_token,
        month,
        limit: 10,
      });
      setLoginActivityStats(result || null);
    } catch (error) {
      console.error(error);
      setLoginActivityStats(null);
    } finally {
      setLoginActivityLoading(false);
    }
  };
  const fetchMonths = async () => {
    try {
      let apiParam = {
        user_token: user_data.user_token,
      };
      const response = await model.getMonthsList(apiParam);
      if (typeof response == typeof undefined) {
        navigate("/re-login");
        return;
      }
      //console.log("Months response =>", response.months);
      setMonths(response.months);
      // Set current month as default
      const currentMonth = new Date().toISOString().slice(0, 7); // e.g. "2025-08"
      const defaultMonth = response.months.find(m => m.value === currentMonth);
      setSelectedMonth(defaultMonth || response.months[0]);

      // Fetch data after setting the month
      const monthValue = defaultMonth?.value || response.months[0]?.value;
      getCounts(monthValue);
      getAmStats(monthValue);
    } catch (error) {
        console.error(error);
    }
  }

  useEffect(() => {
    if (loginActivityAccessLoading || !selectedMonth?.value) {
      return;
    }
    getLoginActivityStats(selectedMonth.value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loginActivityAccessLoading, canViewLoginActivityDashboard, selectedMonth?.value]);
  const getRecruiterRanking = (data) => {
    if (!Array.isArray(data)) return { top: [], bottom: [] };

    const sorted = [...data].sort((a, b) => b.total_submissions - a.total_submissions);
    console.log("Sorted Recruiters:", sorted);

    return {
      top: sorted.slice(0, 5),            // Top 10
      bottom: sorted.slice(-5).reverse()  // Bottom 10
    };
  };

  useEffect(() => {
    fetchUsers();
    //getCounts();
    fetchMonths();
  }, []);


  return (
    <AdminLayout>
      <Helmet>
        <title>Dashboard - Digital CMS</title>
      </Helmet>

      <div className="main-content app-content">
        <div className="container-fluid">

          {/* Page Header */}
          <div className="d-md-flex d-block align-items-center justify-content-between my-4 page-header-breadcrumb">
            <div>
              <h1 className="page-title fw-semibold fs-22 mb-0">Dashboard</h1>
              <p className="text-muted mb-0">{greeting}</p>
            </div>
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item active">
                  <a href="#" onClick={(e) => e.preventDefault()}>Dashboard</a>
                </li>
              </ol>
            </nav>
          </div>

          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-2">Loading dashboard...</p>
            </div>
          ) : (
            <>
              <WorkflowHelpBanner stage="dashboard" />
              <div className='row'>
              {Array.isArray(months) && months.length > 0 && selectedMonth ? (
                <div className="mb-3" style={{ maxWidth: 300 }}>
                  <select
                    className="form-select"
                    value={selectedMonth.value}
                    onChange={(e) => {
                      const selected = months.find(m => m.value === e.target.value);
                      setSelectedMonth(selected);
                      getCounts(selected.value);
                      getAmStats(selected.value);
                      getLoginActivityStats(selected.value);
                    }}
                  >
                    {months.map((month) => (
                      <option key={month.value} value={month.value}>
                        {month.label}
                      </option>
                    ))}
                  </select>
                </div>
              ) : (
                <div className="mb-3" style={{ maxWidth: 300 }}>
                  <div className="form-control">Loading months...</div>
                </div>
              )}
              </div>
              {canViewLoginActivityDashboard && (
                <div className="row px-2">
                  <div className="col-md-12 mb-4" style={{ border: "2px solid black" }}>
                    <UserLoginFrequencyCharts
                      stats={loginActivityStats}
                      loading={loginActivityLoading}
                      monthLabel={selectedMonth?.label}
                    />
                  </div>
                </div>
              )}
              {checkPermission('meetings', 'view') && (
                <div className="row px-2">
                  <div className="col-md-12 mb-4" style={{ border: "2px solid black" }}>
                    <AmMeetingStats amStats={amStats} loading={amLoading} />
                  </div>
                </div>
              )}
              <div className="row px-2">
                <div className="col-md-12 mb-4 " style={ { border:"2px solid black" } }>
                  <DashboardTables
                    dashboardCounts={dashboardCounts}
                    recruiterFilter={pipelineRecruiterFilter}
                    managerFilter={pipelineManagerFilter}
                    onRecruiterFilterChange={onPipelineRecruiterFilterChange}
                    onManagerFilterChange={onPipelineManagerFilterChange}
                  />
                </div>
                <div className="col-md-12 mb-4" style={{ border: "2px solid black" }}>
                  <ActivePositionsPie dashboardCounts={filteredPipelineCounts} />
                </div>
              </div>
            {user_data.urole_type === 'admin' && (
              <div className="row px-2">
                <div className="col-md-6 mb-4" style={{ border: "2px solid black", borderRight: "none" }}>
                  <RecruiterConversionGraph
                    recruiters={getRecruiterRanking(dashboardCounts || []).top}
                    title="Top 5 Recruiters "
                  />
                </div>
                <div className="col-md-6 mb-4" style={{ border: "2px solid black" }}>
                  <RecruiterConversionGraph
                    recruiters={getRecruiterRanking(dashboardCounts || []).bottom}
                    title="Bottom 5 Recruiters "
                  />
                </div>
              </div>
            )}
              <div className="row px-2">
              {user_data.urole_type !== 'admin' && (
                <div className="col-md-12 mb-4" style={ { border:"2px solid black" } }>
                  <ConversionGraph dashboardCounts={dashboardCounts} />
                </div>
              )}
              </div>
              {interviewsList && interviewsList.length > 0 ? (
  <div className="row px-2">
    <div className="col-md-12 mb-4" style={{ border: "2px solid black" }}>
      <h5 className="fw-semibold mb-3 mt-2 px-2">Upcoming Interviews</h5>
      <div className="table-responsive mb-3">
        <table className="table table-bordered align-middle text-center table-sm">
          <thead className="stylishback" style={{ backgroundColor: "#0d6efd", color: "#fff", fontWeight: "bold" }}>
            <tr>
              <th>#</th>
              
              <th>Candidate</th>
              <th>Client</th>
              <th>Position</th>
              <th>Mode</th>
              <th>Date</th>
              <th>Time</th>
              <th>Recruiter</th>
            </tr>
          </thead>
          <tbody>
            {interviewsList.map((interview, index) => (
              <tr
                key={interview._interview_id || index}
                role={interview.interview_unique_id ? "button" : undefined}
                className={interview.interview_unique_id ? "cursor-pointer" : ""}
                onClick={() => {
                  if (interview.interview_unique_id) {
                    navigate(`/interviews/${interview.interview_unique_id}/view`, {
                      state: {
                        data: interview,
                        module: {
                          title: "Interviews",
                          singularTitle: "Interview",
                          key: "interviews",
                          route: "interviews",
                          primaryCol: "_interview_id",
                        },
                      },
                    });
                  }
                }}
              >
                <td>{index + 1}</td>
                <td>{interview.candidate_name || '-'}</td>
                <td>{interview.client_name || '-'}</td>
                <td>{interview.requirement_name || '-'}</td>
                <td>{interview.interview_mode=='f2f' ? 'Face 2 Face' : interview.interview_mode || '-'}</td>
                <td>
                  {interview.interview_date
                    ? new Date(interview.interview_date).toLocaleDateString('en-IN', {
                        day: '2-digit',
                        month: 'short',
                        year: 'numeric',
                      })
                    : '-'}
                </td>
                <td>
                  {interview.interview_time
                    ? new Date(
                        `1970-01-01T${interview.interview_time}`
                      ).toLocaleTimeString('en-IN', {
                        hour: '2-digit',
                        minute: '2-digit',
                      })
                    : '-'}
                </td>
                <td>{interview.recruiter_name || '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  </div>
) : (
  <div className="row px-2">
    <div className="col-md-12 mb-4" style={{ border: "2px solid black" }}>
      <h5 className="fw-semibold mb-3 mt-2 px-2">Upcoming Interviews</h5>
      <p className="text-center text-muted mb-3">No upcoming interviews found.</p>
    </div>
  </div>
)}

            </>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}

export default Dashboard;
